import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Loader2, ShieldAlert, Car } from "lucide-react";
import DriverAttestationCard from "@/components/DriverAttestationCard";
import axios from "axios";

/**
 * DriverVerificationPage · v2.6.8 batch M
 * Public (no-auth) page at /verify/driver/:driverId. Riders open this when
 * they want to confirm their assigned driver's background-check status
 * (e.g., by scanning the QR on the driver's car or via the in-app rider
 * verification button). Returns minimal driver info, never PII.
 */
export default function DriverVerificationPage() {
  const { driverId } = useParams();
  const [driver, setDriver] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    axios.get(`${base}/api/drivers/${driverId}/public-verification`)
      .then(r => setDriver(r.data))
      .catch(e => setErr(e?.response?.data?.detail || "Driver not found"));
  }, [driverId]);

  if (err) {
    return (
      <div className="min-h-screen bg-[#0c0d10] text-zinc-100 flex items-center justify-center p-4">
        <Card className="bg-zinc-900 border-red-500/40 p-6 max-w-sm w-full text-center" data-testid="verification-error">
          <ShieldAlert className="h-10 w-10 text-red-400 mx-auto mb-2"/>
          <div className="font-bold mb-1">Unable to verify driver</div>
          <p className="text-xs text-zinc-400">{err}</p>
        </Card>
      </div>
    );
  }
  if (!driver) {
    return (
      <div className="min-h-screen bg-[#0c0d10] text-zinc-100 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-amber-400"/>
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-[#0c0d10] text-zinc-100 p-4">
      <div className="max-w-sm mx-auto space-y-4 pt-6" data-testid="rider-verification">
        <div className="text-center">
          <div className="text-[10px] uppercase tracking-[0.3em] text-amber-400 font-bold">Buller Taxi · Verify Your Driver</div>
          <div className="text-2xl font-extrabold mt-2">{driver.name}</div>
          {driver.vehicle_make && (
            <div className="text-xs text-zinc-400 mt-1 flex items-center justify-center gap-1">
              <Car className="h-3.5 w-3.5"/>
              {driver.vehicle_year || ""} {driver.vehicle_make} {driver.vehicle_model || ""} {driver.vehicle_color ? `· ${driver.vehicle_color}` : ""}
              {driver.plate_number ? ` · ${driver.plate_number}` : ""}
            </div>
          )}
        </div>
        {driver.photo_url && (
          <div className="mx-auto rounded-full w-24 h-24 overflow-hidden border-2 border-amber-400/40 bg-zinc-800">
            <img src={driver.photo_url} alt={driver.name} className="w-full h-full object-cover"/>
          </div>
        )}
        <DriverAttestationCard driver={driver} variant="rider"/>
        <p className="text-[10px] text-zinc-500 text-center mt-3">
          This verification page is public so any passenger can check their driver's status. No personal contact info is shared.
        </p>
      </div>
    </div>
  );
}
